/**
 * AspspPicker (Phase 2, §7.5) — a searchable single-select list of banks from
 * `useAspsps('DE')`. Filtering is client-side over name + BIC; the chosen bank
 * (`value` = ASPSP `name`) is highlighted. Used in the setup wizard's step 3.
 */
import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '../../../components/ui/Input.tsx';
import type { AspspOut } from '../../../lib/types.ts';

export interface AspspPickerProps {
  aspsps: AspspOut[];
  value: string | null;
  onSelect: (aspsp: AspspOut) => void;
  disabled?: boolean;
}

export function AspspPicker({ aspsps, value, onSelect, disabled }: AspspPickerProps) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const list = needle
      ? aspsps.filter(
          (a) =>
            a.name.toLowerCase().includes(needle) || (a.bic ?? '').toLowerCase().includes(needle),
        )
      : aspsps;
    return [...list].sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }, [aspsps, query]);

  return (
    <div className="space-y-3">
      <div className="group relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 transition-colors duration-150 group-focus-within:text-sky-600 dark:group-focus-within:text-sky-400" />
        <Input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Bank suchen …"
          aria-label="Bank suchen"
          disabled={disabled}
          className="pl-10"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="px-1 py-6 text-center text-sm text-slate-400 dark:text-slate-500">
          Keine Bank gefunden.
        </p>
      ) : (
        <ul className="max-h-72 divide-y divide-slate-100 overflow-y-auto border-y border-slate-100 dark:divide-slate-800/70 dark:border-slate-800/70">
          {filtered.map((aspsp) => {
            const active = aspsp.name === value;
            return (
              <li key={`${aspsp.name}:${aspsp.country}`}>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onSelect(aspsp)}
                  aria-pressed={active}
                  className={`flex w-full items-center justify-between gap-3 px-3 py-3 text-left text-sm transition-colors duration-150 disabled:cursor-not-allowed ${
                    active
                      ? 'bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300'
                      : 'text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800/60'
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{aspsp.name}</span>
                    {aspsp.bic ? (
                      <span className="block truncate text-xs text-slate-400 dark:text-slate-500">
                        {aspsp.bic}
                      </span>
                    ) : null}
                  </span>
                  {aspsp.beta ? (
                    <span className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                      Beta
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
